import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useInventory, VIEW_MODES } from '../../contexts/InventoryContext';
import {
  getTypeIcon, getTypeLabel, getTypeOrder,
} from '../../hooks/useInventoryData';
import { COLUMNS, resolveVisibleColumns, cellStyle, minTableWidth, COLUMN_MIN_WIDTH } from '../../contexts/inventoryColumns';
import DocumentRow from './DocumentRow';
import HelpLink from '../help/HelpLink';

// First-paint estimates only. Every mounted row is measured by ResizeObserver,
// and the measured height is kept under the row's identity key (below) — so a
// row keeps its height across re-sorts and across leaving and re-entering the
// viewport.
const ROW_HEIGHT = 52;
const EXPANDED_ROW_HEIGHT = 500;
const GROUP_HEADER_HEIGHT = 44;

export default function InventoryTable({
  docs = [],
  allDocs = null,
  caseHasDocuments = true,
  typeGroups = {},
  caseId,
  folderPaths,
  onDocChange,
  onShowDetail,
  onShowViewer,
  hashCountMap = {},
  dimmedDocIds = null,
  onUseAsMatch = null,
  matchBusy = false,
}) {
  const { dispatch, ...state } = useInventory();
  const parentRef = useRef(null);
  const selectAllRef = useRef(null);
  const lastSelectedIdRef = useRef(null);

  const orderedIds = useMemo(() => docs.map(d => d.id), [docs]);

  // documents cut out of each compilation file, from the UNFILTERED list (a filter may hide them)
  const cutChildCounts = useMemo(() => {
    const counts = new Map();
    for (const d of allDocs || docs) {
      if (d.container_id && d.page_start != null) counts.set(d.container_id, (counts.get(d.container_id) || 0) + 1);
    }
    return counts;
  }, [allDocs, docs]);

  const visibleColumns = useMemo(
    () => resolveVisibleColumns(state.visibleColumns),
    [state.visibleColumns]
  );

  // Floor the table width so the Name column never crushes below readability;
  // the wrapper scrolls horizontally in lockstep (header + body) once the
  // visible columns no longer fit the viewport.
  const columnWidths = state.columnWidths || {};
  const tableMinWidth = useMemo(
    () => minTableWidth(visibleColumns, columnWidths),
    [visibleColumns, columnWidths]
  );

  // Adjustable columns: drag a header cell's right edge. The width follows the pointer
  // through the header cell's own inline style and is committed to the inventory state
  // (and the person's saved preferences) on release; double-click the edge to reset.
  const [resizing, setResizing] = useState(null);           // column id while a drag is on
  const startResize = useCallback((e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const cell = e.currentTarget.parentElement;
    const startX = e.clientX;
    const startW = cell.getBoundingClientRect().width;
    const min = id === 'name' ? 160 : COLUMN_MIN_WIDTH;
    let width = startW;
    const move = (ev) => {
      width = Math.max(min, Math.min(1200, startW + (ev.clientX - startX)));
      cell.style.width = `${width}px`;
      cell.style.flex = 'none';
      cell.style.minWidth = '0';
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setResizing(null);
      dispatch({ type: 'SET_COLUMN_WIDTH', id, width });
    };
    setResizing(id);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, [dispatch]);

  const handleCheckboxToggle = useCallback((id, shiftKey) => {
    if (shiftKey && lastSelectedIdRef.current && lastSelectedIdRef.current !== id) {
      const startIdx = orderedIds.indexOf(lastSelectedIdRef.current);
      const endIdx = orderedIds.indexOf(id);
      if (startIdx !== -1 && endIdx !== -1) {
        const [lo, hi] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
        const next = new Set(state.selected);
        for (let i = lo; i <= hi; i++) next.add(orderedIds[i]);
        dispatch({ type: 'SELECT_ALL', ids: Array.from(next) });
        lastSelectedIdRef.current = id;
        return;
      }
    }
    dispatch({ type: 'SELECT_DOC', id });
    lastSelectedIdRef.current = id;
  }, [orderedIds, state.selected, dispatch]);

  const items = buildItemList(state.viewMode, docs, typeGroups, state.expandedGroups, state.expandedDocs);

  // Virtual items are keyed by identity — a document's id or a group's key —
  // never by position. The virtualizer keeps its element and size caches by
  // this key. With position keys, a refetch that re-sorted the mounted rows
  // (the list refreshes every 30 s and on focus; a rename or a cut file's
  // documents appearing reorder it) left those caches pointing at the wrong
  // nodes, and the next row to mount at a moved row's old index un-observed
  // that live row: nothing measured it any more, so its expanded panel painted
  // over the rows below it. One row, until a reload.
  const getItemKey = useCallback((index) => {
    const item = items[index];
    if (!item) return index;
    return item.type === 'group-header' ? `gh:${item.typeKey}` : item.doc.id;
  }, [items]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    getItemKey,
    estimateSize: useCallback(
      (index) => {
        const item = items[index];
        if (!item) return ROW_HEIGHT;
        if (item.type === 'group-header') return GROUP_HEADER_HEIGHT;
        return state.expandedDocs.has(item.doc.id) ? EXPANDED_ROW_HEIGHT : ROW_HEIGHT;
      },
      [items, state.expandedDocs]
    ),
    overscan: 8,
    // Measure to the fraction. Rows are fractional heights (a wrapped title
    // is 84.3 px, say) and the library's default rounds each one, so stacked
    // rows drifted by up to half a pixel — neighbours overlapped or gapped
    // and their hairline borders shimmered under the pointer and on scroll.
    measureElement: useCallback((el, entry) => {
      const box = entry?.borderBoxSize?.[0];
      if (box && box.blockSize > 0) return box.blockSize;
      return el.getBoundingClientRect().height;
    }, []),
  });

  const selectedVisibleCount = docs.reduce(
    (count, d) => count + (state.selected.has(d.id) ? 1 : 0),
    0
  );
  const allSelected = docs.length > 0 && selectedVisibleCount === docs.length;
  const partiallySelected = selectedVisibleCount > 0 && selectedVisibleCount < docs.length;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = partiallySelected;
    }
  }, [partiallySelected]);

  return (
    <div className={`inv-table-wrapper ${resizing ? 'is-resizing' : ''}`}>
      {/* Header row — driven by visibleColumns from the registry. */}
      <div className="inv-table-header" style={{ minWidth: tableMinWidth }}>
        <label className="inv-head-check" title="Select all visible documents">
          <input
            ref={selectAllRef}
            type="checkbox"
            checked={allSelected}
            aria-label="Select all visible documents"
            onChange={() => dispatch({
              type: 'SELECT_ALL',
              ids: !allSelected ? docs.map(d => d.id) : [],
            })}
          />
        </label>
        {visibleColumns.map((id) => {
          const col = COLUMNS[id];
          return (
            <span
              key={id}
              className={`inv-th inv-th--${id}`}
              style={cellStyle(col, columnWidths[id])}
            >
              {col.header}
              <span
                className={`inv-th-resizer ${resizing === id ? 'is-active' : ''}`}
                data-testid={`col-resizer-${id}`}
                role="separator"
                aria-orientation="vertical"
                aria-label={`Resize the ${typeof col.header === 'string' ? col.header : id} column`}
                title="Drag to resize · double-click to reset"
                onPointerDown={(e) => startResize(e, id)}
                onDoubleClick={(e) => { e.stopPropagation(); dispatch({ type: 'SET_COLUMN_WIDTH', id, width: null }); }}
              />
            </span>
          );
        })}
        <span className="inv-th inv-th--chevron" />
      </div>

      {items.length === 0 && (
        <div className="inv-empty">
          {caseHasDocuments ? (
            // Docs exist but the active filters hide them all — say that,
            // not "no documents", so the user looks at their filters.
            <>No documents match the current filters.</>
          ) : (
            <>
              No documents yet — upload your discovery to get started.{' '}
              <HelpLink slug="uploading-documents" label="Uploading documents">
                New here? Learn how uploads work →
              </HelpLink>
            </>
          )}
        </div>
      )}

      <div ref={parentRef} className="inv-scroll-area" style={{ minWidth: tableMinWidth }}>
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((vItem) => {
            const item = items[vItem.index];
            if (!item) return null;

            if (item.type === 'group-header') {
              const isOpen = state.expandedGroups.has(item.typeKey);
              return (
                <div
                  key={`gh-${item.typeKey}`}
                  ref={virtualizer.measureElement}
                  data-index={vItem.index}
                  className="inv-group-header"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${vItem.start}px)`,
                  }}
                  onClick={() => dispatch({ type: 'TOGGLE_GROUP_EXPAND', key: item.typeKey })}
                >
                  <span className={`inv-row-chevron ${isOpen ? 'inv-row-chevron--open' : ''}`}>
                    ▸
                  </span>
                  <span className="inv-group-icon">{getTypeIcon(item.typeKey)}</span>
                  <span className="inv-group-label">{getTypeLabel(item.typeKey)}</span>
                  <span className="inv-group-count">{item.count}</span>
                </div>
              );
            }

            return (
              <DocumentRow
                key={item.doc.id}
                doc={item.doc}
                visibleColumns={visibleColumns}
                caseId={caseId}
                folderPaths={folderPaths}
                onDocChange={onDocChange}
                onShowDetail={onShowDetail}
                onShowViewer={onShowViewer}
                onCheckboxToggle={handleCheckboxToggle}
                hashCountMap={hashCountMap}
                cutChildCounts={cutChildCounts}
                isDimmed={dimmedDocIds?.has(item.doc.id) || false}
                onUseAsMatch={onUseAsMatch}
                matchBusy={matchBusy}
                measureRef={virtualizer.measureElement}
                dataIndex={vItem.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${vItem.start}px)`,
                  zIndex: state.expandedDocs.has(item.doc.id) ? 1 : 0,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function buildItemList(viewMode, docs, typeGroups, expandedGroups) {
  if (viewMode === VIEW_MODES.LIST) {
    return docs.map(doc => ({ type: 'doc', doc }));
  }

  const order = getTypeOrder();
  const items = [];

  const groupKeys = Object.keys(typeGroups).sort(
    (a, b) => (order.indexOf(a) === -1 ? 99 : order.indexOf(a))
          - (order.indexOf(b) === -1 ? 99 : order.indexOf(b))
  );

  for (const key of groupKeys) {
    const groupDocs = typeGroups[key] || [];
    items.push({
      type: 'group-header',
      typeKey: key,
      count: groupDocs.length,
    });
    if (expandedGroups.has(key)) {
      for (const doc of groupDocs) {
        items.push({ type: 'doc', doc });
      }
    }
  }

  return items;
}
